import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fetchDetailedResults } from "../../api";
import toast, { Toaster } from 'react-hot-toast';
import { 
  ChartBarIcon,
  ArrowLeftIcon,
  CheckCircleIcon,
  XCircleIcon,
  MinusCircleIcon,
  UserIcon,
  PhoneIcon,
  IdentificationIcon,
  TrophyIcon,
  DocumentChartBarIcon
} from '@heroicons/react/24/outline';

const DetailedResults = () => {
  const { uid } = useParams();
  const navigate = useNavigate();
  
  const [results, setResults] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [totalMarks, setTotalMarks] = useState(0);
  const [username, setUsername] = useState("");
  const [parentsNumber, setParentsNumber] = useState("");
  const [totalCorrect, setTotalCorrect] = useState(0);
  const [totalIncorrect, setTotalIncorrect] = useState(0);
  const [totalNotAnswered, setTotalNotAnswered] = useState(0);
  const [hasData, setHasData] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchResults = async () => {
      if (!uid) {
        setError("User ID is missing");
        setLoading(false);
        return;
      }

      setLoading(true);
      console.log("🔍 Fetching results for UID:", uid);

      try {
        const data = await fetchDetailedResults(uid);
        console.log("📥 API Response:", data);

        if (data && data.error) {
          console.error("❌ API Error:", data.error);
          
          if (data.user_exists) {
            setError(`No exam found: ${data.message || 'Student has not attempted any exam yet'}`);
          } else {
            setError(data.error);
          }
          
          toast.error(data.error);
          setHasData(false);
          setLoading(false);
          return;
        }
        
        if (data && data.results && Array.isArray(data.results) && data.results.length > 0) {
          console.log("✅ Results loaded:", data.results.length);
          
          setHasData(true);
          setResults(data.results);
          setSummary(data.summary || {});
          setTotalCorrect(data.total_correct || 0);
          setTotalIncorrect(data.total_incorrect || 0);
          setTotalNotAnswered(data.total_not_answered || 0);
          
          const marks = (data.total_correct || 0) * 4;
          setTotalMarks(marks);
          
          setUsername(data.student_name || "Unknown");
          setParentsNumber(data.parents_number || "N/A");
          toast.success('Results loaded successfully');
        } else {
          console.warn("⚠️ No results");
          setError("No results data available");
          setHasData(false);
          toast.warning('No results available');
        }
      } catch (err) {
        console.error("❌ Fetch Error:", err);
        setError(err.message || "Failed to load results");
        setHasData(false);
        toast.error(err.message || "Failed to load results");
      } finally {
        setLoading(false);
      }
    };

    fetchResults();
  }, [uid]);

  if (loading) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-blue-50 via-white to-purple-50 flex justify-center items-center z-50">
        <div className="text-center bg-white p-10 rounded-2xl shadow-xl">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-xl font-semibold text-gray-800">Loading results...</p>
        </div>
      </div>
    );
  }

  if (error || !hasData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-6">
        <Toaster position="top-right" />
        <div className="max-w-2xl mx-auto mt-20">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <DocumentChartBarIcon className="h-12 w-12 text-gray-400" />
            </div>
            <h2 className="text-3xl font-bold text-gray-800 mb-4">No Results Available</h2>
            <p className="text-gray-600 mb-8 text-lg">
              {error || "This user has not submitted any exam answers yet."}
            </p>
            <button 
              onClick={() => navigate('/admin/result')}
              className="inline-flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-8 py-4 rounded-xl font-semibold hover:from-blue-700 hover:to-indigo-700 transform hover:scale-105 transition-all shadow-md"
            >
              <ArrowLeftIcon className="h-5 w-5" />
              <span>Back to Results</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  const percentage = results.length > 0 ? ((totalCorrect / results.length) * 100).toFixed(2) : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-6">
      <Toaster position="top-right" />
      
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center">
                <ChartBarIcon className="h-7 w-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">Detailed Results</h1>
                <p className="text-sm text-gray-500">Complete performance analysis</p>
              </div>
            </div>
            <button 
              onClick={() => navigate('/admin/result')}
              className="flex items-center space-x-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all border border-gray-300"
            >
              <ArrowLeftIcon className="h-5 w-5" />
              <span>Back</span>
            </button>
          </div>
          
          {/* Student Info */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 bg-gradient-to-br from-gray-50 to-white p-6 rounded-xl border border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <UserIcon className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-xs text-gray-600 font-medium">Student Name</p>
                <p className="text-lg font-bold text-gray-800">{username}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <PhoneIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-gray-600 font-medium">Parent Number</p>
                <p className="text-lg font-bold text-gray-800">{parentsNumber}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
                <IdentificationIcon className="h-6 w-6 text-indigo-600" />
              </div>
              <div>
                <p className="text-xs text-gray-600 font-medium">User ID</p>
                <p className="text-lg font-bold text-gray-800">{uid}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl shadow-md p-6 text-white">
            <div className="text-sm font-semibold opacity-90 mb-2">Total Questions</div>
            <div className="text-4xl font-bold">{results.length}</div>
          </div>
          
          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-2xl shadow-md p-6 text-white">
            <div className="flex items-center space-x-2 mb-2">
              <CheckCircleIcon className="h-4 w-4" />
              <div className="text-sm font-semibold opacity-90">Correct</div>
            </div>
            <div className="text-4xl font-bold">{totalCorrect}</div>
          </div>
          
          <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-2xl shadow-md p-6 text-white">
            <div className="flex items-center space-x-2 mb-2">
              <XCircleIcon className="h-4 w-4" />
              <div className="text-sm font-semibold opacity-90">Incorrect</div>
            </div>
            <div className="text-4xl font-bold">{totalIncorrect}</div>
          </div>
          
          <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-2xl shadow-md p-6 text-white">
            <div className="flex items-center space-x-2 mb-2">
              <MinusCircleIcon className="h-4 w-4" />
              <div className="text-sm font-semibold opacity-90">Not Answered</div>
            </div>
            <div className="text-4xl font-bold">{totalNotAnswered}</div>
          </div>
          
          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl shadow-md p-6 text-white">
            <div className="flex items-center space-x-2 mb-2">
              <TrophyIcon className="h-4 w-4" />
              <div className="text-sm font-semibold opacity-90">Total Marks</div>
            </div>
            <div className="text-4xl font-bold">{totalMarks}</div>
          </div>
          
          <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-2xl shadow-md p-6 text-white">
            <div className="text-sm font-semibold opacity-90 mb-2">Percentage</div>
            <div className="text-4xl font-bold">{percentage}%</div>
          </div>
        </div>

        {/* Summary by Group */}
        {Object.keys(summary).length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
              <DocumentChartBarIcon className="h-6 w-6 mr-2 text-blue-600" />
              Summary by Question Group
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gradient-to-r from-gray-50 to-gray-100 border-b-2 border-gray-200">
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Question Group
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Correct
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Incorrect
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Not Answered
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {Object.keys(summary).map((group, idx) => (
                    <tr key={idx} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 text-sm font-semibold text-gray-800">{group}</td>
                      <td className="px-6 py-4 text-center">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-green-100 text-green-800">
                          {summary[group].correct}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-red-100 text-red-800">
                          {summary[group].incorrect}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-yellow-100 text-yellow-800">
                          {summary[group].not_answered}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center text-sm font-bold text-gray-800">
                        {summary[group].total}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Questions Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Question-wise Results</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gradient-to-r from-gray-50 to-gray-100 border-b-2 border-gray-200">
                  <th className="px-4 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">#</th>
                  <th className="px-4 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider min-w-[300px]">Question</th>
                  <th className="px-4 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider min-w-[200px]">Correct Answer</th>
                  <th className="px-4 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider min-w-[200px]">Your Answer</th>
                  <th className="px-4 py-4 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Group</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {results.map((item, index) => {
                  const isCorrect = parseInt(item.is_correct);
                  const selectedOption = item.selected_option || 'Not Answered';
                  const isNotAnswered = parseInt(item.selected_option_id) === 0;
                  const rowBgClass = isCorrect === 1 ? 'bg-green-50' : isNotAnswered ? 'bg-yellow-50' : 'bg-red-50';
                  
                  return (
                    <tr key={index} className={`${rowBgClass} hover:opacity-80 transition-opacity`}>
                      <td className="px-4 py-4 text-sm font-bold text-gray-800">{index + 1}</td>
                      <td className="px-4 py-4 text-sm text-gray-800">
                        <div dangerouslySetInnerHTML={{ __html: item.question }} />
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-800">
                        <div dangerouslySetInnerHTML={{ __html: item.correct_answer }} />
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-800">
                        {isNotAnswered ? (
                          <span className="text-yellow-800 font-bold italic">⊘ Not Answered</span>
                        ) : (
                          <div dangerouslySetInnerHTML={{ __html: selectedOption }} />
                        )}
                      </td>
                      <td className="px-4 py-4 text-center">
                        {isNotAnswered ? (
                          <span className="inline-flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-bold bg-yellow-100 text-yellow-800">
                            <MinusCircleIcon className="h-4 w-4" />
                            <span>Skipped</span>
                          </span>
                        ) : isCorrect === 1 ? (
                          <span className="inline-flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-bold bg-green-100 text-green-800">
                            <CheckCircleIcon className="h-4 w-4" />
                            <span>Correct</span>
                          </span>
                        ) : (
                          <span className="inline-flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-bold bg-red-100 text-red-800">
                            <XCircleIcon className="h-4 w-4" />
                            <span>Wrong</span>
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-700 font-semibold">{item.question_group}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetailedResults;
